/**
 * The homeowner's inspection report, built from a scan's existing-conditions
 * results (claude-docs/2026-10-15-inspection-report-design.md). The server
 * freezes it under a share token; the contractor's review page builds it too,
 * to show what the homeowner will see.
 *
 * It carries ONLY what was agreed for the homeowner — condition, defects with
 * their severity and photo, material, plans — never the style read, the era
 * flags or the devices: the output is assembled field by field, never by
 * copying a doc. Firestore stores it, so nothing in it is `undefined` and no
 * array directly holds an array. Pure.
 */
const { activeReview } = require('./inspectionReview');
const {
  codeLabel,
  materialLabel,
  finishLabel,
  deviceFinishLabel,
  roomTitle,
  surfaceKindLabel,
  countedList,
} = require('./inspectionLabels');
const {
  roomOutline,
  hasFace,
  letterWalls,
  keyPlan,
  roomPlan,
} = require('./inspectionPlan');
const { roomSchedule } = require('./inspectionItems');
const { withInspectedOpenings } = require('./openingTypeApply');
const { roomMakeup } = require('./inspectionMakeup');
const { storeyStructure } = require('./inspectionStructure');
const {
  evidencePhoto,
  shotViews,
  deviceView,
  sharedViews,
  ROW_LISTS,
} = require('./inspectionViews');
const { getFloorY } = require('../kitchen-assembly/floorY');
const { STYLE_ERAS } = require('./vocabulary');

// the decades a room's finishes may be said to read as: never timeless or unknown
const DECADES = new Set(
  STYLE_ERAS.filter((e) => e !== 'timeless' && e !== 'unknown')
);

/**
 * A room's parts by the decade their finishes read as, from the run's
 * `roomFinishes` entry for it (the depth service asked the room three times and
 * kept a part only when every answer agreed). → [{ part, decade }], in the
 * order asked
 */
function finishAgeOf(entry) {
  return ((entry && entry.parts) || [])
    .filter((p) => p && p.part && DECADES.has(p.decade))
    .map((p) => ({ part: codeLabel(p.part), decade: p.decade }));
}

// 2: each room's `items` and `devices` (claude-docs/2026-10-15-inspection-report-items-design.md)
// 3: a key plan's rooms name their part of the report (`room`); each room
// says what it is made of (`makeup`)
// 4: every plan carries its walls, rooms' walls, doors and windows (`structure`)
// 5: each device its finish and a fixture's grade
// 6: each room the decade its finishes read as, where every answer agreed (`finishAge`)
// 7: each room's doors and windows in its schedule (`D1`, `W1`), by kind
const REPORT_VERSION = 7;
const SEVERITY_RANK = { safety: 0, repair: 1, cosmetic: 2 };
const GRADE_RANK = { poor: 0, fair: 1, good: 2 };
const KIND_RANK = {
  floor: 0,
  ceiling: 1,
  'wall-face': 2,
  object: 3,
  door: 4,
  window: 5,
};
// a key outside its table sorts after every key in it
const rank = (table, key) =>
  key in table ? table[key] : Object.keys(table).length;
const byText = (a, b) => String(a).localeCompare(String(b));

const roomKey = (d) => d.roomId || `index-${d.roomIndex}`;

/**
 * A room's docs from the newest run that asked about it (by `measuredAt`, as
 * `reportRunId` reads it). A re-run writes a doc for every target of the rooms it
 * asks about, reusing the prior doc of the same surface; a prior doc no target
 * claimed is left behind — a wall whose ends moved, a stretch of wall from before
 * a face was one target across its doors (2026-10-19) — and describes a surface
 * the room no longer has. Runs may ask about some rooms only (`roomIndexes`), so
 * this is per room. Docs with no `runId` are kept only in a room no run touched.
 */
function currentRun(group) {
  let newest = null;
  for (const d of group) {
    if (
      d.runId &&
      (!newest || String(d.measuredAt) > String(newest.measuredAt))
    )
      newest = d;
  }
  return newest ? group.filter((d) => d.runId === newest.runId) : group;
}

/**
 * A scan's docs grouped by the room they were asked in — a room carved from
 * another room's capture shares its `roomIndex` but not its `roomId` — in
 * `roomIndex` order, each room's from its newest run (`currentRun`). The
 * report's `rooms` come out in this order.
 */
function roomGroups(docs) {
  const groups = new Map();
  for (const d of docs) {
    if (!groups.has(roomKey(d))) groups.set(roomKey(d), []);
    groups.get(roomKey(d)).push(d);
  }
  return [...groups.values()]
    .map(currentRun)
    .sort(
      (a, b) =>
        Number(a[0].roomIndex) - Number(b[0].roomIndex) ||
        byText(roomKey(a[0]), roomKey(b[0]))
    );
}

/** One surface as the report reads it, after the contractor's review. */
function readSurface(doc) {
  const review = activeReview(doc);
  const observed = doc.verdict === 'observed';
  const findings = observed
    ? (doc.findings || [])
        // `duplicateOf`: another surface reported the same defect and keeps it
        // (`fold_across_targets` in the depth service) — counted there, once
        .filter(
          (f) =>
            f && f.id && !f.duplicateOf && !review.hiddenFindings.includes(f.id)
        )
        .sort(
          (a, b) =>
            rank(SEVERITY_RANK, a.severity) - rank(SEVERITY_RANK, b.severity) ||
            byText(a.id, b.id)
        )
    : [];
  // the contractor says it is not built in (a dresser the scanner called
  // storage): drawn as furniture, out of every list and the summary
  if (doc.kind === 'object' && review.notFixed) {
    return { doc, review, findings: [], grade: null, status: 'not-fixed' };
  }
  const modelGrade = doc.condition && doc.condition.grade;
  const grade = !observed
    ? null
    : review.grade || (modelGrade in GRADE_RANK ? modelGrade : null);
  let status = 'good';
  if (!observed) status = 'not-seen';
  else if (findings.length || grade !== 'good') status = 'problem';
  return { doc, review, findings, grade, status };
}

const worstSeverity = (s) =>
  s.findings.length ? rank(SEVERITY_RANK, s.findings[0].severity) : 3;
const kindOrder = (a, b) =>
  rank(KIND_RANK, a.doc.kind) - rank(KIND_RANK, b.doc.kind) ||
  byText(a.label, b.label);

const TABLE_KINDS = new Set(['floor', 'ceiling', 'wall-face']);

/**
 * Every floor, ceiling and wall of the room as one table, in plan order —
 * floors, ceilings, then the walls by letter — each with its material, its
 * condition and its defects' numbers: what a reader takes in at a glance, the
 * defects themselves listed below it. A room with two of a kind numbers them
 * ("Ceiling 1", "Ceiling 2"). The review page's rows also name their doc.
 */
function surfaceTable(surfaces, letters, numbersOf, forReview) {
  const rows = surfaces
    .filter((s) => TABLE_KINDS.has(s.doc.kind))
    .map((s, position) => ({ s, position }))
    .sort(
      (a, b) =>
        rank(KIND_RANK, a.s.doc.kind) - rank(KIND_RANK, b.s.doc.kind) ||
        byText(
          letters.get(a.s.doc.targetId) || '~',
          letters.get(b.s.doc.targetId) || '~'
        ) ||
        a.position - b.position
    )
    .map(({ s }) => ({
      label: s.label,
      wall: letters.get(s.doc.targetId) || null,
      material: materialLabel(s.doc.material),
      finish: s.status === 'not-seen' ? '' : finishLabel(s.doc),
      status: s.status === 'not-seen' ? 'not-seen' : 'observed',
      grade: s.status === 'not-seen' ? null : s.grade,
      limitedView: Boolean(
        s.doc.condition && s.doc.condition.confidence === 'low'
      ),
      defects: numbersOf.get(s.doc.targetId) || [],
      views: shotViews([s.doc]),
      ...(forReview ? { targetId: s.doc.targetId } : {}),
    }));
  const count = new Map();
  for (const r of rows) count.set(r.label, (count.get(r.label) || 0) + 1);
  const seen = new Map();
  for (const r of rows) {
    if (count.get(r.label) < 2) continue;
    const n = (seen.get(r.label) || 0) + 1;
    seen.set(r.label, n);
    r.label = `${r.label} ${n}`;
  }
  return rows;
}

/** "wall C", "walls A and C", "walls A, C and D". */
function namedWalls(letters) {
  if (letters.length === 1) return `wall ${letters[0]}`;
  return `walls ${letters.slice(0, -1).join(', ')} and ${letters[letters.length - 1]}`;
}

/**
 * The surfaces in good condition, in one line: "Floor, walls A and C,
 * cabinet" — counted by kind, a lettered wall named by its letter.
 */
function goodLine(good, letters) {
  const lettered = (s) => letters.has(s.doc.targetId);
  const isPlane = (s) => s.doc.kind === 'floor' || s.doc.kind === 'ceiling';
  const counted = (list) =>
    countedList(list.map((s) => surfaceKindLabel(s.doc)));
  const walls = good.filter(lettered).map((s) => letters.get(s.doc.targetId));
  const parts = [
    counted(good.filter((s) => isPlane(s))),
    walls.length ? namedWalls(walls) : '',
    counted(good.filter((s) => !isPlane(s) && !lettered(s))),
  ].filter(Boolean);
  const text = parts
    .map((p, i) => (i ? p.charAt(0).toLowerCase() + p.slice(1) : p))
    .join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** One room's part of the report, and the counts the summary adds up. */
function buildRoom(docs, roomDoc, storeyRooms, drawing, finishes, forReview) {
  const surfaces = docs.map(readSurface);
  const byTarget = new Map(surfaces.map((s) => [s.doc.targetId, s]));
  const outline = roomDoc ? roomOutline(roomDoc) : null;
  // every wall the plan can draw is lettered, a good one too, so every letter
  // on the plan leads to a row and a re-inspection keeps a wall's letter
  const faces = surfaces
    .filter((s) => s.doc.kind === 'wall-face' && hasFace(s.doc))
    .map((s) => s.doc);
  const letters = letterWalls(faces, outline);
  const schedule = roomSchedule({
    room: roomDoc,
    docs,
    wallLetter: (id) => letters.get(id) || null,
    surfaceOf: (doc) => byTarget.get(doc.targetId),
  });
  const itemOf = new Map(
    schedule.items.flatMap((item) => item.targetIds.map((id) => [id, item]))
  );
  for (const s of surfaces) {
    const letter = letters.get(s.doc.targetId);
    const item = itemOf.get(s.doc.targetId);
    if (letter) s.label = `Wall ${letter}`;
    else if (item) s.label = `${surfaceKindLabel(s.doc)} ${item.tag}`;
    else s.label = surfaceKindLabel(s.doc);
  }

  let n = 0;
  const dots = [];
  const listed = surfaces
    .filter((s) => s.status === 'problem')
    .sort(
      (a, b) =>
        worstSeverity(a) - worstSeverity(b) ||
        rank(GRADE_RANK, a.grade) - rank(GRADE_RANK, b.grade) ||
        kindOrder(a, b)
    )
    .map((s) => ({
      targetId: s.doc.targetId,
      surfaceLabel: s.label,
      // the letter of its wall on the plan, which a click on that tag finds
      wall: letters.get(s.doc.targetId) || null,
      material: materialLabel(s.doc.material),
      grade: s.grade,
      limitedView: Boolean(
        s.doc.condition && s.doc.condition.confidence === 'low'
      ),
      findings: s.findings.map((f) => {
        n += 1;
        dots.push({ n, severity: f.severity, world: f.world });
        const other = f.type === 'other';
        return {
          id: f.id,
          n,
          label: other
            ? f.description || codeLabel('other')
            : codeLabel(f.type),
          description: other ? null : f.description || null,
          severity: f.severity,
          photo: s.review.hiddenPhotos.includes(f.id)
            ? null
            : evidencePhoto(s.doc, f),
        };
      }),
    }));

  const good = surfaces.filter((s) => s.status === 'good').sort(kindOrder);
  const notSeen = surfaces
    .filter((s) => s.status === 'not-seen')
    .sort(kindOrder);
  const walls = faces
    .map((doc) => ({ letter: letters.get(doc.targetId), doc }))
    .sort((a, b) => byText(a.letter, b.letter));
  const named = docs.find((d) => d.roomName);
  const numbersOf = new Map(
    listed.map((s) => [s.targetId, s.findings.map((f) => f.n)])
  );
  const devices = schedule.devices.filter((d) => !d.hidden);
  const viewsOfDevice = (d) => {
    const doc = byTarget.get(d.targetId).doc;
    const view = deviceView(
      doc,
      (doc.devices || []).find((v) => v && v.id === d.id)
    );
    return view ? [view] : [];
  };
  const layers = {
    // the room's floor height: the floor plan draws an object whose bottom
    // is 48" above it dashed, and the report's plans draw what it draws
    floorY: roomDoc ? getFloorY(roomDoc) : null,
    items: schedule.items.map((i) => ({
      key: `item:${i.tag}`,
      tag: i.tag,
      point: i.point,
      boxes: i.boxes.map((b) => ({
        frame: b.frame,
        corners: b.corners,
        object: b.object,
      })),
    })),
    devices: devices
      .filter((d) => d.point)
      .map((d) => ({
        key: `device:${d.tag}`,
        tag: d.tag,
        letters: d.letters,
        type: d.type,
        subtype: d.subtype,
        normal: d.normal,
        x: d.point.x,
        z: d.point.z,
      })),
    furniture: schedule.furniture,
    structure: drawing,
  };
  const room = {
    roomName: roomTitle(
      named ? named.roomName : roomDoc && roomDoc.name,
      docs[0].roomIndex
    ),
    plan: outline
      ? roomPlan(roomDoc.id, storeyRooms, walls, dots, layers)
      : null,
    surfaces: listed,
    surfaceRows: surfaceTable(surfaces, letters, numbersOf, forReview),
    // the schedule: every fixed item, tagged, with what the inspection found
    items: schedule.items.map((i) => ({
      tag: i.tag,
      label: i.label,
      material: i.material,
      finish: i.finish,
      grade: i.grade,
      status: i.status,
      defects: i.targetIds.flatMap((id) => numbersOf.get(id) || []),
      views: shotViews(i.targetIds.map((id) => byTarget.get(id).doc)),
      // what the review page's switches write to
      ...(forReview
        ? {
            targetIds: i.targetIds,
            run: i.key.startsWith('run:'),
            letters: i.letters,
          }
        : {}),
    })),
    // what the recording saw, never a full count; the review page also gets the
    // ones the contractor hid, to undo
    devices: forReview
      ? schedule.devices.map((d) => ({
          tag: d.tag,
          label: d.label,
          where: d.where,
          wall: d.wall,
          finish: deviceFinishLabel(d.finish),
          grade: d.grade,
          hidden: d.hidden,
          targetId: d.targetId,
          id: d.id,
          views: viewsOfDevice(d),
        }))
      : devices.map((d) => ({
          tag: d.tag,
          label: d.label,
          where: d.where,
          wall: d.wall,
          finish: deviceFinishLabel(d.finish),
          grade: d.grade,
          views: viewsOfDevice(d),
        })),
    ...(forReview
      ? {
          // with their pictures, for the contractor to see what they hid
          released: schedule.released.map((r) => ({
            ...r,
            views: shotViews([byTarget.get(r.targetId).doc]),
          })),
          // every lettered wall, so the review page names a good one too
          wallLetters: Object.fromEntries(letters),
        }
      : {}),
    // what it is made of: what the recording saw of its surfaces and fixed items
    makeup: roomMakeup(
      surfaces
        .filter((s) => TABLE_KINDS.has(s.doc.kind) && s.status !== 'not-seen')
        .map((s) => s.doc),
      schedule.items
        .flatMap((i) => i.targetIds.map((id) => byTarget.get(id)))
        .filter((s) => s.status !== 'not-seen' && s.status !== 'not-fixed')
        .map((s) => s.doc),
      // and the devices the recording saw, never one the contractor hid
      devices
    ),
    // what decade its finishes read as: the decade a look belongs to, never
    // when it was put in
    finishAge: finishAgeOf(finishes),
    good: goodLine(good, letters),
    notSeen: notSeen.map((s) => ({
      targetId: s.doc.targetId,
      surfaceLabel: s.label,
      wall: letters.get(s.doc.targetId) || null,
    })),
  };
  const grades = { good: 0, fair: 0, poor: 0 };
  for (const s of surfaces) if (s.grade in grades) grades[s.grade] += 1;
  return { room, grades };
}

/**
 * The report. `docs`: the scan's existing-conditions docs; `rooms`: its room
 * docs (`{ id, name, roomIndex, story, floors }`), for the plans;
 * `structure`: the structure doc of the checkpoint the rooms come from, for the
 * plans' walls, doors and windows, or null; `roomFinishes`: the run's
 * `roomFinishes`, each room's finishes by decade, keyed as the rooms are, or
 * null; `contractor`: `{ name, company, email }` or null.
 */
function buildInspectionReport({
  docs,
  rooms,
  projectName,
  scanName,
  contractor,
  structure = null,
  roomFinishes = null,
  // the contractor's review page only: keeps what the contractor hid, to undo.
  // A shared copy is never built this way.
  forReview = false,
}) {
  const planRooms = (rooms || [])
    .map((r) => ({
      id: r.id,
      name: roomTitle(r.name, r.roomIndex),
      story: r.story ?? null,
      outline: roomOutline(r),
    }))
    .filter((r) => r.outline);
  const roomsById = new Map((rooms || []).map((r) => [r.id, r]));

  const groups = roomGroups(docs);
  // the plans' doors and windows typed from the answers this report lists, so a
  // plan never draws a swing arc beside a row saying "Pocket doors"
  const drawn = withInspectedOpenings(structure, groups.flat());
  // a room's part of the report, by its id, for the key plans to open
  const partOf = new Map(
    groups.map((g, i) => [g[0].roomId, i]).filter(([id]) => id)
  );
  // each storey's walls, doors and windows, worked out once for its plans
  const drawings = new Map();
  const drawingOf = (story) => {
    if (!drawings.has(story)) {
      drawings.set(story, storeyStructure(drawn, rooms, story));
    }
    return drawings.get(story);
  };
  const built = groups.map((group) => {
    const roomDoc = roomsById.get(group[0].roomId) || null;
    const story = roomDoc ? (roomDoc.story ?? null) : null;
    return buildRoom(
      group,
      roomDoc,
      planRooms.filter((r) => r.story === story),
      drawingOf(story),
      (roomFinishes || {})[roomKey(group[0])] || null,
      forReview
    );
  });

  const grades = { good: 0, fair: 0, poor: 0 };
  const findings = { safety: 0, repair: 0, cosmetic: 0 };
  const top = [];
  built.forEach(({ room, grades: g }, roomOrder) => {
    for (const k of Object.keys(grades)) grades[k] += g[k];
    for (const s of room.surfaces) {
      for (const f of s.findings) {
        if (f.severity in findings) findings[f.severity] += 1;
        if (f.severity === 'safety' || f.severity === 'repair') {
          top.push({
            roomOrder,
            roomName: room.roomName,
            surfaceLabel: s.surfaceLabel,
            n: f.n,
            label: f.label,
            severity: f.severity,
          });
        }
      }
    }
  });
  top.sort(
    (a, b) =>
      rank(SEVERITY_RANK, a.severity) - rank(SEVERITY_RANK, b.severity) ||
      a.roomOrder - b.roomOrder ||
      a.n - b.n
  );

  const stories = [...new Set(planRooms.map((r) => r.story))].sort(
    (a, b) => (a === null) - (b === null) || a - b
  );
  const measured = docs
    .map((d) => d.measuredAt)
    .filter((t) => typeof t === 'string')
    .sort();

  return {
    version: REPORT_VERSION,
    project: { name: projectName || '', scanName: scanName || '' },
    inspectedAt: measured.length ? measured[measured.length - 1] : null,
    contractor: contractor
      ? {
          name: contractor.name || '',
          company: contractor.company || '',
          email: contractor.email || '',
        }
      : null,
    summary: { grades, findings },
    topFindings: top.map(({ roomOrder, ...rest }) => rest),
    keyPlans: stories.map((story) => ({
      story,
      plan: keyPlan(
        planRooms
          .filter((r) => r.story === story)
          .map((r) => ({ ...r, room: partOf.get(r.id) ?? null })),
        drawingOf(story)
      ),
    })),
    rooms: built.map((b) => b.room),
  };
}

/**
 * The report a homeowner reads: each photo's evidence path swapped for its
 * cropped copy's download URL from `photos` ("<targetId>/<findingId>" → url),
 * or null when it has none, and each row's pictures for the ones uploaded
 * (`sharedViews`) — no evidence path reaches a shared copy.
 */
function attachPhotoUrls(report, photos) {
  const withViews = (room) =>
    Object.fromEntries(
      ROW_LISTS.map((list) => [
        list,
        (room[list] || []).map((r) => ({
          ...r,
          views: sharedViews(r.views, photos),
        })),
      ])
    );
  return {
    ...report,
    rooms: report.rooms.map((room) => ({
      ...room,
      ...withViews(room),
      surfaces: room.surfaces.map((s) => ({
        ...s,
        findings: s.findings.map((f) => {
          const url = f.photo ? photos[`${s.targetId}/${f.id}`] : null;
          return {
            ...f,
            photo: typeof url === 'string' && url ? { url } : null,
          };
        }),
      })),
    })),
  };
}

module.exports = {
  REPORT_VERSION,
  roomGroups,
  buildInspectionReport,
  attachPhotoUrls,
};
